import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Avatar, CircularProgress, Alert, Button,
    TextField, FormControl, InputLabel, Select, MenuItem, Chip, LinearProgress, Divider, Tabs, Tab,
    Collapse, IconButton, Pagination, Autocomplete
} from '@mui/material';
import { Search, Refresh, ExpandMore, ExpandLess, TrendingUp, TrendingDown, OpenInNew } from '@mui/icons-material';

const getRankBadgeColor = (rank) => {
    if (!rank) return '#9e9e9e';
    if (rank === 1) return '#ffd700';
    if (rank <= 3) return '#c0c0c0';
    if (rank <= 10) return '#cd7f32';
    return '#4caf50';
};

const getPerformanceLevel = (improvement) => {
    if (improvement > 5) return { label: 'Ausgezeichnet', color: '#4caf50' };
    if (improvement > 2) return { label: 'Sehr gut', color: '#66bb6a' };
    if (improvement > 0) return { label: 'Gut', color: '#81c784' };
    if (improvement === 0) return { label: 'Stabil', color: '#ffb74d' };
    return { label: 'Rückgang', color: '#ef5350' };
};

// Calculate percentage improvement
const getPercentageImprovement = (beforeRank, afterRank) => {
    if (!beforeRank || beforeRank === 0) return 0;
    return Math.round(((beforeRank - afterRank) / beforeRank) * 100);
};

// Rank quality score (0-100, higher is better)
const getRankQualityScore = (rank) => {
    if (!rank) return 0;
    if (rank === 1) return 100;
    if (rank <= 3) return 95;
    if (rank <= 5) return 90;
    if (rank <= 10) return 75;
    if (rank <= 15) return 60;
    if (rank <= 20) return 40;
    return 20;
};

// Weighted performance score (0-100)
const getPerformanceScore = (beforeRank, afterRank) => {
    if (!beforeRank || !afterRank) return 0;
    const percentageImprovement = getPercentageImprovement(beforeRank, afterRank);
    const startingQuality = getRankQualityScore(beforeRank);
    const finalQuality = getRankQualityScore(afterRank);

    return Math.round(
        (percentageImprovement * 0.4) +
        (startingQuality * 0.3) +
        (finalQuality * 0.3)
    );
};

// Get impact label based on score
const getImpactLabel = (score) => {
    if (score >= 80) return 'Hot';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Low';
};

// Get impact color based on score
const getImpactColor = (score) => {
    if (score >= 80) return '#ff5722';
    if (score >= 60) return '#4caf50';
    if (score >= 40) return '#ff9800';
    return '#f44336';
};

// Format date in German format (DD.MM.YYYY)
const formatDateDE = (dateValue) => {
    if (!dateValue) return '';
    // Handle both Date objects and strings
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (isNaN(date.getTime())) return ''; // Invalid date
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const ImprovementChip = ({ value, size = 'small' }) => (
    <Chip
        icon={value > 0 ? <TrendingUp style={{ fontSize: 14 }} /> : value < 0 ? <TrendingDown style={{ fontSize: 14 }} /> : undefined}
        label={`${value > 0 ? '+' : ''}${value}`}
        size={size}
        sx={{
            background: value > 0
                ? 'linear-gradient(135deg, #e8f5e8, #c8e6c9)'
                : value < 0
                ? 'linear-gradient(135deg, #ffebee, #ffcdd2)'
                : 'linear-gradient(135deg, #f5f5f5, #eeeeee)',
            color: value > 0 ? '#2e7d32' : value < 0 ? '#c62828' : '#616161',
            fontWeight: 'bold',
            border: 'none',
            fontSize: '0.7rem',
            height: 22,
        }}
    />
);

const RankBadge = ({ rank, size = 44 }) => (
    <Box sx={{
        width: size, height: size, borderRadius: '50%',
        background: getRankBadgeColor(rank),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 'bold', fontSize: size > 36 ? '1rem' : '0.8rem',
        flexShrink: 0,
    }}>
        {rank || '—'}
    </Box>
);

const cardBase = {
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
    border: '1px solid #e0e0e0',
    position: 'relative',
    overflow: 'visible',
};

const topBar = (color) => ({
    '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0,
        height: '4px', background: color,
        borderTopLeftRadius: '16px', borderTopRightRadius: '16px',
    }
});

// ─── TOKEN HELPER ────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('auth_token') || localStorage.getItem('token');

// ─── MONTHS SELECTOR ─────────────────────────────────────────────────────────
const MonthsSelect = ({ value, onChange, disabled }) => (
    <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Zeitraum</InputLabel>
        <Select value={value} label="Zeitraum" onChange={e => onChange(e.target.value)} disabled={disabled}>
            <MenuItem value={1}>1 Monat</MenuItem>
            <MenuItem value={2}>2 Monate</MenuItem>
            <MenuItem value={3}>3 Monate</MenuItem>
            <MenuItem value={6}>6 Monate</MenuItem>
            <MenuItem value={12}>12 Monate</MenuItem>
        </Select>
    </FormControl>
);

// Reason why this keyword is a top performer
const getRankLabel = (rank) => {
    if (!rank) return null;
    if (rank === 1) return { label: '🥇 Platz 1', color: '#f57f17' };
    if (rank <= 3) return { label: `🥈 Top 3 (Rang ${rank})`, color: '#9e9e9e' };
    if (rank <= 5) return { label: `Top 5 (Rang ${rank})`, color: '#6d4c41' };
    if (rank <= 10) return { label: `Top 10 (Rang ${rank})`, color: '#2e7d32' };
    return null;
};

// ─── KEYWORD ROW (used in Nach Unternehmen and Übersicht business expand) ────
const KeywordRow = ({ kw, last }) => {
    const perf = getPerformanceLevel(kw.improvement);
    const rankLabel = getRankLabel(kw.currentRank);
    const percentageImprovement = kw.previousRank ? getPercentageImprovement(kw.previousRank, kw.currentRank) : 0;
    const performanceScore = kw.previousRank ? getPerformanceScore(kw.previousRank, kw.currentRank) : 0;

    return (
        <>
            <Box display="flex" alignItems="flex-start" gap={1.5} py={1.5}>
                <RankBadge rank={kw.currentRank} size={40} />
                <Box flex={1} minWidth={0}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.25 }} noWrap>
                        {kw.keyword}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                        {kw.previousRank
                            ? `Rang ${kw.previousRank}(${formatDateDE(kw.previousRankDate)}) → Rang ${kw.currentRank}(${formatDateDE(kw.currentRankDate)})`
                            : `Aktuell Rang ${kw.currentRank} · kein Vergleichswert`}
                        {kw.dataPoints ? ` · ${kw.dataPoints} Messpunkte` : ''}
                    </Typography>
                    {/* Reason badges */}
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                        {kw.improvement > 0 && (
                            <Chip
                                label={`↑ Verbessert um ${kw.improvement} Plätze`}
                                size="small"
                                sx={{ background: '#e8f5e8', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem', height: 18 }}
                            />
                        )}
                        {rankLabel && (
                            <Chip
                                label={rankLabel.label}
                                size="small"
                                sx={{ background: `${rankLabel.color}18`, color: rankLabel.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }}
                            />
                        )}
                        {kw.improvement === 0 && !rankLabel && (
                            <Chip label={perf.label} size="small" sx={{ background: `${perf.color}20`, color: perf.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
                        )}
                    </Box>
                </Box>
                {/* OPTION A: Percentage improvement and performance score */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                    {percentageImprovement > 0 && (
                        <Chip
                            label={`↑${percentageImprovement}%`}
                            size="small"
                            sx={{
                                background: percentageImprovement > 30 ? '#4caf50' : '#ff9800',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                            }}
                        />
                    )}
                </Box>
                <ImprovementChip value={kw.improvement} />
            </Box>
            {!last && <Divider />}
        </>
    );
};

// ─── BUSINESS ROW (used in Nach Keyword and Übersicht keyword expand) ────────
const BusinessRow = ({ biz, keyword, last }) => (
    <>
        <Box display="flex" alignItems="center" gap={1.5} py={1}>
            <RankBadge rank={biz.currentRank} size={36} />
            <Box flex={1} minWidth={0}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }} noWrap>
                    {biz.businessName}
                </Typography>
                {keyword && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                        Keyword: {keyword}
                    </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                    {biz.previousRank ? `Rang ${biz.previousRank}(${formatDateDE(biz.previousRankDate)}) → ${biz.currentRank}(${formatDateDE(biz.currentRankDate)})` : `Aktuell Rang ${biz.currentRank}`}
                    {biz.dataPoints ? ` · ${biz.dataPoints} Datenpunkte` : ''}
                </Typography>
            </Box>
            <ImprovementChip value={biz.improvement} />
        </Box>
        {!last && <Divider />}
    </>
);

// ─── EXPANDABLE SECTION CARD ─────────────────────────────────────────────────
const ExpandableCard = ({ title, subtitle, chipLabel, chipColor, topBarGradient, expanded, onToggle, children }) => (
    <Card sx={{ ...cardBase, ...topBar(topBarGradient), mb: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1} minWidth={0}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }} noWrap>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={1} ml={1}>
                    {chipLabel && (
                        <Chip label={chipLabel} size="small" sx={{ background: `${chipColor}20`, color: chipColor, fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
                    )}
                    <IconButton size="small" onClick={onToggle}>
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box mt={1.5}>
                    {children}
                </Box>
            </Collapse>
        </CardContent>
    </Card>
);

// In-memory cache for keyword performance data (no localStorage quota issues)
const apiCache = new Map();

// ════════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
const KeywordPerformancePage = () => {
    const [activeTab, setActiveTab] = useState(0);

    // Tab 0: Overview — all 4 periods pre-loaded from cache
    const [overviewLoading, setOverviewLoading]   = useState(false);
    const [allPeriods, setAllPeriods]             = useState({});   // { 1: data, 3: data, 6: data, 12: data }
    const [overviewMonths, setOverviewMonths]     = useState(3);
    const [overviewError, setOverviewError]       = useState(null);
    const [expandedBiz, setExpandedBiz]           = useState({});
    const [expandedKw, setExpandedKw]             = useState({});
    const [topBusinessesLimit, setTopBusinessesLimit] = useState(10);
    const [topKeywordsLimit, setTopKeywordsLimit] = useState(20);

    // Derived: current period data based on selected months
    const overviewData = allPeriods[overviewMonths] || null;

    // Tab 1: Nach Unternehmen
    const [busName, setBusName]           = useState('');
    const [busMonths, setBusMonths]       = useState(3);
    const [busLoading, setBusLoading]     = useState(false);
    const [busData, setBusData]           = useState(null);
    const [busError, setBusError]         = useState(null);
    const [busSuggestions, setBusSuggestions] = useState([]);

    // Tab 2: Nach Keyword
    const [kwKeyword, setKwKeyword]   = useState('');
    const [kwMonths, setKwMonths]     = useState(3);
    const [kwLoading, setKwLoading]   = useState(false);
    const [kwError, setKwError]       = useState(null);
    const [kwResults, setKwResults]   = useState(null);
    const [kwPage, setKwPage]         = useState(1);
    const [kwSuggestions, setKwSuggestions] = useState([]);
    const KW_PER_PAGE = 12;

    // Fetch all businesses and keywords on component mount
    useEffect(() => {
        const fetchSuggestions = async () => {
            const token = getToken();
            if (!token) return;

            try {
                const [busRes, kwRes] = await Promise.all([
                    fetch('/api/analytics/all-businesses', { headers: { Authorization: `Bearer ${token}` } }),
                    fetch('/api/analytics/all-keywords', { headers: { Authorization: `Bearer ${token}` } })
                ]);

                if (busRes.ok) {
                    const busData = await busRes.json();
                    setBusSuggestions(busData.businesses || []);
                }

                if (kwRes.ok) {
                    const kwData = await kwRes.json();
                    setKwSuggestions(kwData.keywords || []);
                }
            } catch (err) {
                console.error('Error fetching suggestions:', err);
            }
        };

        fetchSuggestions();
    }, []); // eslint-disable-line

    const businessOptions = busSuggestions;
    const keywordOptions = kwSuggestions;

    // Custom filter for autocomplete (case-insensitive substring matching)
    const createFilterOptions = () => (options, state) => {
        const inputValue = state.inputValue.toLowerCase().trim();
        if (!inputValue) return options;
        return options.filter(option => option.toLowerCase().includes(inputValue));
    };

    // Load all periods once on component mount (check in-memory cache first)
    useEffect(() => {
        if (Object.keys(allPeriods).length === 0) {
            if (apiCache.has('keywordPerfCache')) {
                setAllPeriods(apiCache.get('keywordPerfCache'));
            } else {
                loadOverview();
            }
        }
    }, []); // eslint-disable-line


    const loadOverview = useCallback(async () => {
        setOverviewLoading(true);
        setOverviewError(null);
        const token = getToken();
        if (!token) { setOverviewError('Authentifizierung erforderlich.'); setOverviewLoading(false); return; }
        try {
            // Fetch all 4 periods in parallel — all served from cache instantly
            const periods = [1, 3, 6, 12];
            const results = await Promise.all(
                periods.map(m =>
                    fetch(`/api/analytics/top-keywords-overview?months=${m}`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.ok ? r.json() : null)
                        .catch(() => null)
                )
            );
            const periodMap = {};
            periods.forEach((m, i) => { if (results[i]) periodMap[m] = results[i]; });
            setAllPeriods(periodMap);
            // Cache in memory (instant, no quota issues)
            apiCache.set('keywordPerfCache', periodMap);
        } catch (e) { setOverviewError(e.message); }
        finally { setOverviewLoading(false); }
    }, []);

    const loadBusiness = useCallback(async () => {
        if (!busName.trim()) return;
        setBusLoading(true); setBusError(null); setBusData(null);
        const token = getToken();
        if (!token) { setBusError('Authentifizierung erforderlich.'); setBusLoading(false); return; }
        try {
            const res = await fetch(`/api/analytics/business-keyword-performance?businessName=${encodeURIComponent(busName.trim())}&months=${busMonths}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Fehler'); }
            setBusData(await res.json());
        } catch (e) { setBusError(e.message); }
        finally { setBusLoading(false); }
    }, [busName, busMonths]);

    const loadKeyword = useCallback(async () => {
        if (!kwKeyword.trim()) return;
        setKwLoading(true); setKwError(null); setKwResults(null); setKwPage(1);
        const token = getToken();
        if (!token) { setKwError('Authentifizierung erforderlich.'); setKwLoading(false); return; }
        try {
            const res = await fetch(`/api/analytics/keyword-performance?keyword=${encodeURIComponent(kwKeyword.trim())}&months=${kwMonths}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Fehler'); }
            setKwResults(await res.json());
        } catch (e) { setKwError(e.message); }
        finally { setKwLoading(false); }
    }, [kwKeyword, kwMonths]);

    const toggleBiz = (key) => setExpandedBiz(prev => ({ ...prev, [key]: !prev[key] }));
    const toggleKw  = (key) => setExpandedKw(prev => ({ ...prev, [key]: !prev[key] }));

    // ── RENDER ──────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1e293b' }}>
                Keyword Performance Analyse
            </Typography>

            <Card sx={{ ...cardBase, mb: 3 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Übersicht" />
                    <Tab label="Nach Unternehmen" />
                    <Tab label="Nach Keyword" />
                </Tabs>

                <CardContent sx={{ p: 3 }}>

                    {/* ═══════════════════════════════ TAB 0: ÜBERSICHT ═══════════════════════════ */}
                    {activeTab === 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                                <MonthsSelect value={overviewMonths} onChange={v => setOverviewMonths(v)} disabled={overviewLoading} />
                                <FormControl sx={{ minWidth: 140 }}>
                                    <InputLabel>Top Unternehmen</InputLabel>
                                    <Select value={topBusinessesLimit} label="Top Unternehmen" onChange={e => setTopBusinessesLimit(e.target.value)} disabled={overviewLoading}>
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={15}>15</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    startIcon={overviewLoading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                                    onClick={() => {
                                        apiCache.delete('keywordPerfCache');
                                        setAllPeriods({});
                                        loadOverview();
                                    }}
                                    disabled={overviewLoading}
                                >
                                    Aktualisieren
                                </Button>
                                {overviewData?.fromCache && overviewData?.cachedAt && (
                                    <Chip label={`Cache: ${new Date(overviewData.cachedAt).toLocaleString('de-DE')}`} size="small" sx={{ background: '#e3f2fd', color: '#1565c0', fontSize: '0.7rem' }} />
                                )}
                                {!overviewLoading && Object.keys(allPeriods).length > 0 && (
                                    <Chip label={`${Object.keys(allPeriods).length} Zeiträume geladen`} size="small" sx={{ background: '#e8f5e8', color: '#2e7d32', fontSize: '0.7rem' }} />
                                )}
                            </Box>

                            {overviewError && <Alert severity="error" sx={{ mb: 2 }}>{overviewError}</Alert>}

                            {overviewLoading && (
                                <Card sx={{ ...cardBase }}><CardContent sx={{ textAlign: 'center', py: 5 }}>
                                    <CircularProgress size={56} />
                                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Lade Übersichtsdaten...</Typography>
                                </CardContent></Card>
                            )}

                            {!overviewLoading && overviewData && (
                                <Box>
                                    <Grid container spacing={3}>
                                        {/* TOP BUSINESSES */}
                                    <Grid item xs={12} md={12}>
                                        <Card sx={{ ...cardBase, ...topBar('linear-gradient(90deg, #4caf50, #66bb6a)') }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>Top Unternehmen</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Sortiert nach durchschnittlicher Verbesserung · {overviewMonths} Monate
                                                </Typography>

                                                <Box mt={2}>
                                                    {overviewData.topBusinesses.length === 0 && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                                            Keine Unternehmen mit Verbesserungen im gewählten Zeitraum.
                                                        </Typography>
                                                    )}
                                                    {overviewData.topBusinesses.slice(0, topBusinessesLimit).map((biz, i) => {
                                                        const key = `biz-${i}`;
                                                        const positiveCount = biz.positiveCount ?? (biz.keywords || []).filter(k => k.improvement > 0).length;
                                                        const perf = getPerformanceLevel(parseFloat(biz.avgImprovement));
                                                        const improvChip = biz.avgImprovement > 0 ? `Ø +${biz.avgImprovement}` : positiveCount > 0 ? `${positiveCount} verbessert` : null;
                                                        return (
                                                            <Box key={key} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <ExpandableCard
                                                                key={key}
                                                                title={`${biz.businessName} (${biz.googleLocationId})`}
                                                                subtitle={`${biz.keywordCount} Keywords · ${positiveCount} verbessert · Bester: +${biz.bestImprovement}`}
                                                                chipLabel={improvChip}
                                                                chipColor={perf.color}
                                                                topBarGradient={biz.avgImprovement > 0 ? 'linear-gradient(90deg, #4caf50, #66bb6a)' : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)'}
                                                                expanded={!!expandedBiz[key]}
                                                                onToggle={() => toggleBiz(key)}
                                                            >
                                                                {(biz.keywords || []).length > 0 && (
                                                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                                                        {(biz.keywords || []).map((kw, ki) => {
                                                                            const perfScore = kw.previousRank ? getPerformanceScore(kw.previousRank, kw.currentRank) : 0;
                                                                            const percentImprovement = kw.previousRank ? getPercentageImprovement(kw.previousRank, kw.currentRank) : 0;
                                                                            const impactLabel = kw.improvement < 0 ? 'Low' : 'Fair';
                                                                            const impactColor = kw.improvement < 0 ? '#f44336' : kw.improvement > 0 ? '#4caf50' : '#1976d2';

                                                                            return (
                                                                                <Grid item xs={12} sm={6} md={4} key={ki}>
                                                                                    <Card sx={{
                                                                                        ...cardBase,
                                                                                        border: `3px solid ${impactColor}`,
                                                                                        transition: 'all 0.3s ease',
                                                                                        '&:hover': {
                                                                                            transform: 'translateY(-4px)',
                                                                                            boxShadow: `0 8px 16px rgba(0,0,0,0.1)`
                                                                                        }
                                                                                    }}>
                                                                                        <CardContent sx={{ p: 2 }}>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', flex: 1, pr: 1 }} noWrap title={kw.keyword}>
                                                                                                    {kw.keyword}
                                                                                                </Typography>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                                                                    {kw.improvement > 0 ? <TrendingUp style={{ fontSize: 16, color: '#4caf50' }} /> : kw.improvement < 0 ? <TrendingDown style={{ fontSize: 16, color: '#f44336' }} /> : null}
                                                                                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: kw.improvement > 0 ? '#4caf50' : kw.improvement < 0 ? '#f44336' : '#9e9e9e' }}>
                                                                                                        {kw.improvement > 0 ? '+' : ''}{kw.improvement}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            </Box>

                                                                                            <Box sx={{ mb: 1.5 }}>
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                                                                                                    RANG
                                                                                                </Typography>
                                                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                                                    <Box sx={{ flex: 1, background: kw.improvement > 0 ? '#c8e6c9' : kw.improvement < 0 ? '#ffcdd2' : '#bbdefb', p: 1, borderRadius: '4px', textAlign: 'center' }}>
                                                                                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: kw.improvement > 0 ? '#2e7d32' : kw.improvement < 0 ? '#c62828' : '#1565c0' }}>
                                                                                                            {kw.previousRank || '—'}
                                                                                                        </Typography>
                                                                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: kw.improvement > 0 ? '#2e7d32' : kw.improvement < 0 ? '#c62828' : '#1565c0', display: 'block', mt: 0.25 }}>
                                                                                                            {formatDateDE(kw.previousRankDate) || '—'}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                    <Typography sx={{ fontWeight: 700, color: '#ccc' }}>→</Typography>
                                                                                                    <Box sx={{ flex: 1, background: kw.improvement > 0 ? '#4caf50' : kw.improvement < 0 ? '#f44336' : '#1976d2', p: 1, borderRadius: '4px', textAlign: 'center' }}>
                                                                                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'white' }}>
                                                                                                            {kw.currentRank || '—'}
                                                                                                        </Typography>
                                                                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.9)', display: 'block', mt: 0.25 }}>
                                                                                                            {formatDateDE(kw.currentRankDate) || '—'}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                </Box>
                                                                                            </Box>

                                                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', color: '#999' }}>
                                                                                                ID: {biz.googleLocationId || '—'}
                                                                                            </Typography>

                                                                                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee', display: 'flex', gap: 1 }}>
                                                                                                <Button
                                                                                                    variant="outlined"
                                                                                                    size="small"
                                                                                                    fullWidth
                                                                                                    endIcon={<OpenInNew fontSize="small" />}
                                                                                                    onClick={() => window.open(`/ranking/${biz.googleLocationId}?keyword=${encodeURIComponent(kw.keyword)}`, '_blank')}
                                                                                                    sx={{ fontSize: '0.75rem' }}
                                                                                                >
                                                                                                    View Ranking
                                                                                                </Button>
                                                                                            </Box>

                                                                                        </CardContent>
                                                                                    </Card>
                                                                                </Grid>
                                                                            );
                                                                        })}
                                                                    </Grid>
                                                                )}
                                                            </ExpandableCard>
                                                                </Box>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    endIcon={<OpenInNew fontSize="small" />}
                                                                    onClick={() => window.open(`/ranking/${biz.googleLocationId}`, '_blank')}
                                                                    sx={{
                                                                        whiteSpace: 'nowrap',
                                                                        alignSelf: 'flex-start',
                                                                        mt: 0.5
                                                                    }}
                                                                >
                                                                    View on Ranking Tracker
                                                                </Button>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 4, mb: 3 }}>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
Top Keywords
                                        </Typography>
                                        <FormControl sx={{ minWidth: 120 }}>
                                            <InputLabel>Top Keywords</InputLabel>
                                            <Select value={topKeywordsLimit} label="Top Keywords" onChange={e => setTopKeywordsLimit(e.target.value)}>
                                                <MenuItem value={5}>Top 5</MenuItem>
                                                <MenuItem value={10}>Top 10</MenuItem>
                                                <MenuItem value={15}>Top 15</MenuItem>
                                                <MenuItem value={20}>Top 20</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {overviewData.topKeywords && overviewData.topKeywords.length > 0 ? (
                                        <Grid container spacing={2}>
                                            {overviewData.topKeywords.slice(0, topKeywordsLimit).map((kw, idx) => {
                                                // Find best rank from businesses
                                                const bestRankData = kw.businesses?.reduce((best, b) => {
                                                    if (!best || b.currentRank < best.currentRank) return b;
                                                    return best;
                                                }, null);

                                                const bestPreviousRank = bestRankData?.previousRank;
                                                const bestCurrentRank = bestRankData?.currentRank;
                                                const bestPreviousRankDate = bestRankData?.previousRankDate;
                                                const bestCurrentRankDate = bestRankData?.currentRankDate;
                                                const performanceScore = bestPreviousRank ? getPerformanceScore(bestPreviousRank, bestCurrentRank) : 0;
                                                const percentageImprovement = bestPreviousRank ? getPercentageImprovement(bestPreviousRank, bestCurrentRank) : 0;
                                                const bestImprovement = bestRankData?.improvement ?? 0;
                                                const impactLabel = bestImprovement < 0 ? 'Low' : 'Fair';
                                                const impactColor = bestImprovement < 0 ? '#f44336' : bestImprovement > 0 ? '#4caf50' : '#1976d2';

                                                return (
                                                    <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                                                        <Card sx={{
                                                            ...cardBase,
                                                            border: `3px solid ${impactColor}`,
                                                            minHeight: '280px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                transform: 'translateY(-4px)',
                                                                boxShadow: `0 8px 16px rgba(0,0,0,0.1)`
                                                            }
                                                        }}>
                                                            <CardContent sx={{ p: 2 }}>
                                                                {/* Header with keyword and icon + improvement */}
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', flex: 1, pr: 1 }} noWrap title={kw.keyword}>
                                                                        {kw.keyword}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                                        <TrendingUp style={{ fontSize: 16, color: '#4caf50' }} />
                                                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#4caf50' }}>
                                                                            +{bestImprovement}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>

                                                                {/* Rank metrics */}
                                                                <Box sx={{ mb: 1.5 }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                                                                        RANG
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                        <Box sx={{ flex: 1, background: bestImprovement > 0 ? '#c8e6c9' : bestImprovement < 0 ? '#ffcdd2' : '#bbdefb', p: 1, borderRadius: '4px', textAlign: 'center' }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: bestImprovement > 0 ? '#2e7d32' : bestImprovement < 0 ? '#c62828' : '#1565c0' }}>
                                                                                {bestPreviousRank || '—'}<Typography variant="caption" sx={{ fontSize: '0.75rem', color: bestImprovement > 0 ? '#2e7d32' : bestImprovement < 0 ? '#c62828' : '#1565c0' }}>({formatDateDE(bestPreviousRankDate)})</Typography>
                                                                            </Typography>
                                                                        </Box>
                                                                        <Typography sx={{ fontWeight: 700, color: '#ccc' }}>→</Typography>
                                                                        <Box sx={{ flex: 1, background: bestImprovement > 0 ? '#4caf50' : bestImprovement < 0 ? '#f44336' : '#1976d2', p: 1, borderRadius: '4px', textAlign: 'center' }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: bestImprovement > 0 ? 'white' : bestImprovement < 0 ? 'white' : 'white' }}>
                                                                                {bestCurrentRank || '—'}<Typography variant="caption" sx={{ fontSize: '0.75rem', color: bestImprovement > 0 ? 'rgba(255,255,255,0.9)' : bestImprovement < 0 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.9)' }}>({formatDateDE(bestCurrentRankDate)})</Typography>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>


                                                                {/* Details */}
                                                                <Box sx={{ background: '#f9f9f9', p: 1, borderRadius: '4px', mb: 1 }}>
                                                                    {kw.businesses && kw.businesses.length > 0 && (() => {
                                                                        const bestBusiness = kw.businesses.reduce((best, b) => !best || b.improvement > best.improvement ? b : best, null);
                                                                        return (
                                                                            <>
                                                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', color: '#666', mb: 0.25, fontWeight: 600 }}>
                                                                                    {bestBusiness?.businessName || ''}
                                                                                </Typography>
                                                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', color: '#999', mb: 0.75 }}>
                                                                                    ID: {bestBusiness?.googleLocationId || '—'}
                                                                                </Typography>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                    {(!kw.businesses || kw.businesses.length === 0) && (
                                                                        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', color: '#666', mb: 0.75 }}>
                                                                            {kw.businessCount} Unternehmen · {kw.positiveCount ?? 0} verbessert
                                                                        </Typography>
                                                                    )}
                                                                </Box>

                                                                {/* View Ranking Button */}
                                                                {kw.businesses && kw.businesses.length > 0 && (() => {
                                                                    const bestBusiness = kw.businesses.reduce((best, b) => !best || b.improvement > best.improvement ? b : best, null);
                                                                    return (
                                                                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee', display: 'flex', gap: 1 }}>
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                fullWidth
                                                                                endIcon={<OpenInNew fontSize="small" />}
                                                                                onClick={() => window.open(`/ranking/${bestBusiness?.googleLocationId}?keyword=${encodeURIComponent(kw.keyword)}`, '_blank')}
                                                                                sx={{ fontSize: '0.75rem' }}
                                                                            >
                                                                                View Ranking
                                                                            </Button>
                                                                        </Box>
                                                                    );
                                                                })()}
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    ) : (
                                        <Card sx={cardBase}>
                                            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Keine Keywords im Gewählten Zeitraum
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    )}
                                </Box>
                                </Box>
                            )}

                            {!overviewLoading && !overviewData && !overviewError && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6">Klicken Sie auf "Aktualisieren" um Daten zu laden.</Typography>
                                </CardContent></Card>
                            )}
                        </Box>
                    )}

                    {/* ═══════════════════════════ TAB 1: NACH UNTERNEHMEN ════════════════════════ */}
                    {activeTab === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 3 }}>
                                <Autocomplete
                                    freeSolo
                                    value={busName}
                                    onChange={(_, newValue) => setBusName(newValue || '')}
                                    inputValue={busName}
                                    onInputChange={(_, newInputValue) => setBusName(newInputValue)}
                                    options={businessOptions}
                                    filterOptions={createFilterOptions()}
                                    sx={{ flex: 1, minWidth: 250 }}
                                    disabled={busLoading}
                                    noOptionsText="Keine Unternehmen gefunden"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Unternehmensname"
                                            placeholder="z.B. Müller GmbH"
                                            onKeyDown={e => e.key === 'Enter' && loadBusiness()}
                                        />
                                    )}
                                    ListboxProps={{ style: { maxHeight: '300px' } }}
                                />
                                <MonthsSelect value={busMonths} onChange={v => setBusMonths(v)} disabled={busLoading} />
                                <Button variant="contained" startIcon={busLoading ? <CircularProgress size={18} color="inherit" /> : <Search />} onClick={loadBusiness} disabled={busLoading || !busName.trim()}>
                                    Suchen
                                </Button>
                            </Box>

                            {busError && <Alert severity="error" sx={{ mb: 2 }}>{busError}</Alert>}

                            {busLoading && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 5 }}>
                                    <CircularProgress size={56} />
                                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Suche Unternehmen...</Typography>
                                </CardContent></Card>
                            )}

                            {!busLoading && busData && (
                                <>
                                    {/* Business summary header */}
                                    <Card sx={{ ...cardBase, ...topBar('linear-gradient(90deg, #2196f3, #42a5f5)'), mb: 3 }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                                                {busData.businessName}
                                            </Typography>
                                            <Box display="flex" gap={1.5} flexWrap="wrap">
                                                <Chip label={`${busData.keywordCount} Keywords`} size="small" sx={{ background: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                                                <Chip label={`Ø +${busData.avgImprovement}`} size="small" sx={{ background: '#e8f5e8', color: '#2e7d32', fontWeight: 600 }} />
                                                <Chip label={`Best: +${busData.bestImprovement}`} size="small" sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                                                <Chip label={`${busMonths} Monate`} size="small" variant="outlined" />
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Keyword cards */}
                                    <Grid container spacing={2}>
                                        {busData.keywords.map((kw, idx) => {
                                            const perf = getPerformanceLevel(kw.improvement);
                                            return (
                                                <Grid item xs={12} sm={6} lg={4} key={kw.keyword}>
                                                    <Card sx={{
                                                        ...cardBase,
                                                        ...topBar(kw.improvement > 0 ? 'linear-gradient(90deg, #4caf50, #66bb6a)' : kw.improvement < 0 ? 'linear-gradient(90deg, #f44336, #ef5350)' : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)'),
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        border: `1px solid ${kw.improvement > 0 ? '#e8f5e8' : kw.improvement < 0 ? '#ffebee' : '#f0f0f0'}`,
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': { boxShadow: '0 12px 28px rgba(0,0,0,0.12)', transform: 'translateY(-4px)' }
                                                    }}>
                                                        <CardContent sx={{ p: 2.5, flex: 1 }}>
                                                            {/* Keyword name */}
                                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', flex: 1, mr: 1 }}>
                                                                    {kw.keyword}
                                                                </Typography>
                                                                <ImprovementChip value={kw.improvement} />
                                                            </Box>

                                                            <Chip label={perf.label} size="small" sx={{ background: `${perf.color}20`, color: perf.color, fontWeight: 600, fontSize: '0.68rem', height: 20, mb: 2 }} />

                                                            {/* Rank comparison */}
                                                            <Box sx={{ background: 'linear-gradient(135deg, #fafafa, #f5f5f5)', borderRadius: '10px', p: 2, border: '1px solid #eaeaea', mb: 2 }}>
                                                                <Grid container spacing={1}>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            {busMonths}M zuvor
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={kw.previousRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            Aktuell
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={kw.currentRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>

                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={Math.min(Math.abs(kw.improvement) * 10, 100)}
                                                                sx={{ height: 6, borderRadius: 3, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { background: kw.improvement > 0 ? 'linear-gradient(90deg, #4caf50, #66bb6a)' : kw.improvement < 0 ? 'linear-gradient(90deg, #f44336, #ef5350)' : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)', borderRadius: 3 } }}
                                                            />
                                                            {kw.dataPoints > 0 && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block', textAlign: 'center', mt: 1 }}>
                                                                    {kw.dataPoints} Datenpunkte
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                        {busData.googleLocationId && (
                                                            <Box sx={{ borderTop: '1px solid #eee', p: 2, pt: 1.5, display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    endIcon={<OpenInNew fontSize="small" />}
                                                                    onClick={() => window.open(`/ranking/${busData.googleLocationId}?keyword=${encodeURIComponent(kw.keyword)}`, '_blank')}
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                >
                                                                    View Ranking
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </>
                            )}

                            {!busLoading && !busName && !busData && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Unternehmensname eingeben</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Geben Sie einen Unternehmensnamen ein, um alle Keywords und deren Verbesserungen zu sehen.
                                    </Typography>
                                </CardContent></Card>
                            )}
                        </Box>
                    )}

                    {/* ═══════════════════════════════ TAB 2: NACH KEYWORD ════════════════════════ */}
                    {activeTab === 2 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 3 }}>
                                <Autocomplete
                                    freeSolo
                                    value={kwKeyword}
                                    onChange={(_, newValue) => setKwKeyword(newValue || '')}
                                    inputValue={kwKeyword}
                                    onInputChange={(_, newInputValue) => setKwKeyword(newInputValue)}
                                    options={keywordOptions}
                                    filterOptions={createFilterOptions()}
                                    sx={{ flex: 1, minWidth: 250 }}
                                    disabled={kwLoading}
                                    noOptionsText="Keine Keywords gefunden"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Keyword"
                                            placeholder="z.B. Badezimmerrenovierung"
                                            onKeyDown={e => e.key === 'Enter' && loadKeyword()}
                                        />
                                    )}
                                    ListboxProps={{ style: { maxHeight: '300px' } }}
                                />
                                <MonthsSelect value={kwMonths} onChange={v => setKwMonths(v)} disabled={kwLoading} />
                                <Button variant="contained" startIcon={kwLoading ? <CircularProgress size={18} color="inherit" /> : <Search />} onClick={loadKeyword} disabled={kwLoading || !kwKeyword.trim()}>
                                    Analysieren
                                </Button>
                            </Box>

                            {kwError && <Alert severity="error" sx={{ mb: 2 }}>{kwError}</Alert>}

                            {kwLoading && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 5 }}>
                                    <CircularProgress size={56} />
                                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Analysiere Ranking-Daten...</Typography>
                                </CardContent></Card>
                            )}

                            {kwResults && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    <strong>{kwResults.total} Unternehmen</strong> mit Ranking-Daten für „<strong>{kwResults.keyword}</strong>" in den letzten <strong>{kwResults.months} Monaten</strong>
                                </Alert>
                            )}

                            {!kwLoading && kwResults && kwResults.businesses.length > 0 && (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        {kwResults.businesses.slice((kwPage - 1) * KW_PER_PAGE, kwPage * KW_PER_PAGE).map((biz, idx) => {
                                            const rankIdx = (kwPage - 1) * KW_PER_PAGE + idx;
                                            const perf = getPerformanceLevel(biz.improvement);
                                            return (
                                                <Grid item xs={12} md={6} lg={4} key={biz.googleLocationId || `kw-${rankIdx}`}>
                                                    <Card sx={{
                                                        ...cardBase,
                                                        ...topBar(biz.improvement > 0 ? 'linear-gradient(90deg, #4caf50, #66bb6a)' : biz.improvement < 0 ? 'linear-gradient(90deg, #f44336, #ef5350)' : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)'),
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        border: `1px solid ${biz.improvement > 0 ? '#e8f5e8' : biz.improvement < 0 ? '#ffebee' : '#f0f0f0'}`,
                                                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                                        '&:hover': { boxShadow: '0 20px 40px rgba(0,0,0,0.12)', transform: 'translateY(-8px)' }
                                                    }}>
                                                        <CardContent sx={{ p: 3, flex: 1 }}>
                                                            {/* Rank + improvement */}
                                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                                <Avatar sx={{
                                                                    background: biz.improvement > 0 ? 'linear-gradient(135deg, #4caf50, #2e7d32)' : biz.improvement < 0 ? 'linear-gradient(135deg, #f44336, #c62828)' : 'linear-gradient(135deg, #9e9e9e, #616161)',
                                                                    boxShadow: biz.improvement > 0 ? '0 4px 12px rgba(76,175,80,0.3)' : 'none',
                                                                    fontWeight: 'bold', fontSize: '0.9rem', color: 'white', width: 40, height: 40
                                                                }}>
                                                                    #{rankIdx + 1}
                                                                </Avatar>
                                                                <ImprovementChip value={biz.improvement} />
                                                            </Box>

                                                            {/* Business name */}
                                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5, fontSize: '1.05rem', lineHeight: 1.3 }}>
                                                                {biz.businessName}
                                                            </Typography>

                                                            {/* Keyword badge */}
                                                            <Chip
                                                                label={`🔑 ${kwResults.keyword}`}
                                                                size="small"
                                                                sx={{ background: '#ede7f6', color: '#4527a0', fontWeight: 600, fontSize: '0.68rem', height: 22, mb: 2 }}
                                                            />

                                                            <Chip label={perf.label} size="small" sx={{ background: `${perf.color}20`, color: perf.color, fontWeight: 600, fontSize: '0.68rem', height: 20, mb: 2, ml: 0.5 }} />

                                                            {/* Rank comparison */}
                                                            <Box sx={{ background: 'linear-gradient(135deg, #fafafa, #f5f5f5)', borderRadius: '10px', p: 2, border: '1px solid #eaeaea', mb: 2 }}>
                                                                <Grid container spacing={1}>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            {kwResults.months}M zuvor
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={biz.previousRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            Aktuell
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={biz.currentRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>

                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={Math.min(Math.abs(biz.improvement) * 10, 100)}
                                                                sx={{ height: 8, borderRadius: 4, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { background: biz.improvement > 0 ? 'linear-gradient(90deg, #4caf50, #66bb6a)' : biz.improvement < 0 ? 'linear-gradient(90deg, #f44336, #ef5350)' : 'linear-gradient(90deg, #9e9e9e, #bdbdbd)', borderRadius: 4 } }}
                                                            />

                                                            {biz.trend && biz.trend.length > 0 && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block', textAlign: 'center', mt: 1 }}>
                                                                    {biz.trend.length} Datenpunkte
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                        {biz.googleLocationId && (
                                                            <Box sx={{ borderTop: '1px solid #eee', p: 2, pt: 1.5, display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    endIcon={<OpenInNew fontSize="small" />}
                                                                    onClick={() => window.open(`/ranking/${biz.googleLocationId}?keyword=${encodeURIComponent(kwResults.keyword)}`, '_blank')}
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                >
                                                                    View Ranking
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>

                                    {Math.ceil(kwResults.businesses.length / KW_PER_PAGE) > 1 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                            <Pagination count={Math.ceil(kwResults.businesses.length / KW_PER_PAGE)} page={kwPage} onChange={(_, v) => setKwPage(v)} color="primary" showFirstButton showLastButton />
                                        </Box>
                                    )}
                                </>
                            )}

                            {!kwLoading && kwKeyword && (!kwResults || kwResults.businesses.length === 0) && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Keine Daten gefunden</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Für das Keyword „<strong>{kwKeyword}</strong>" wurden keine Ranking-Daten gefunden.
                                    </Typography>
                                </CardContent></Card>
                            )}

                            {!kwLoading && !kwKeyword && !kwResults && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Keyword eingeben</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Geben Sie ein Keyword ein, um alle Unternehmen mit deren Ranking-Verbesserungen zu sehen.
                                    </Typography>
                                </CardContent></Card>
                            )}
                        </Box>
                    )}

                </CardContent>
            </Card>
        </Box>
    );
};

export default KeywordPerformancePage;
